import { defineStore } from 'pinia'
import adminApi from '@/api/admin'
import type {
  NewUser,
  OrganizationAttributes,
  NewClient,
  AdminUser,
  SimpleStripeUser,
  AdminApiUsage,
  ReportResult,
  AdminInPortfolioStats
} from 'common'
import { coreClient } from '@/api/coreclient'

export const useAdminStore = defineStore('admin', {
  state: () => ({
    allUsers: [] as AdminUser[],
    allStripeUsers: [] as SimpleStripeUser[],
    allStripeUsersLoading: false,
    apiUsage: [] as AdminApiUsage[],
    inPortfolioStats: [] as AdminInPortfolioStats[]
  }),
  actions: {
    async initAllUsers() {
      this.allUsers = []
      this.allUsers = await adminApi.getAllUsers()
    },
    async initAllStripeUsers() {
      if (!this.allStripeUsersLoading) {
        this.allStripeUsersLoading = true
        this.allStripeUsers = await adminApi.getAllSimpleStripeUsers()
        this.allStripeUsersLoading = false
      }
    },
    async initApiUsage() {
      this.apiUsage = []
      this.apiUsage = await adminApi.getApiUsage(30)
    },
    async initInPortfolioStats() {
      this.inPortfolioStats = []
      this.inPortfolioStats = await adminApi.getInPortfolioStats()
    },
    adminUnsubscribeEmail(email: string) {
      return adminApi.adminUnsubscribeEmail(email)
    },
    createNewUser(data: NewUser) {
      return adminApi.createNewUser(data).then((user) => {
        if (user) {
          this.allUsers = [user].concat(this.allUsers.filter((_) => _.id !== user.id))
        }
      })
    },
    createNewClient(data: NewClient) {
      // TODO - add these to the allUsers and allStripeUsers lists.
      return adminApi.createNewClient(data)
    },
    listOrganizationMembers(userId: string) {
      return adminApi.listOrganizationMembers(userId)
    },
    removeFromOrganization(userId: string, toRemoveUserId: string, toTransferUserId?: string) {
      return adminApi.removeFromOrganization(userId, toRemoveUserId, toTransferUserId)
    },
    getOrganization(userId: string) {
      return adminApi.getOrganization(userId)
    },
    getOrganizationsByProductId(productId?: string) {
      return adminApi.getOrganizationsByProductId(productId)
    },
    updateOrganization(userId: string, id: number, org: Omit<OrganizationAttributes, 'id'>) {
      return adminApi.updateOrganization(userId, id, org).then((newOrg) => {
        for (const user of this.allUsers) {
          if (user.owner_user_id === org.owner_user_id) {
            user.display_name = org.display_name
            user.owner_user_id = org.owner_user_id
          }
        }
        return newOrg
      })
    },
    invitePeople(userId: string, emails: string[]) {
      return adminApi.invitePeople(userId, emails).then(() => {
        return this.listOrganizationMembers(userId)
      })
    },
    resendInviteEmail(userId: string, email: string) {
      return adminApi.resendInviteEmail(userId, email)
    },
    extendLastSub(userId: string, extension: number) {
      return adminApi.extendLastSub(userId, extension)
    },
    patchSubscription(
      subscriptionId: string,
      trialEnd: string,
      products: { productId: string; quantity: number }[]
    ) {
      return adminApi.patchSubscription(subscriptionId, trialEnd, products)
    },
    async getReport(reportId: number): Promise<ReportResult> {
      const client = await coreClient()
      const result = await client.admin.getReport(reportId)
      return result.data
    },
    copyPropertyReport(reportId: number) {
      return adminApi.copyPropertyReport(reportId)
    },
    copyMarketAnalytics(reportId: number) {
      return adminApi.copyMarketAnalytics(reportId)
    },
    copyDataset(datasetId: number) {
      return adminApi.copyDataset(datasetId)
    },
    copyRentRoll(rentRollId: number) {
      return adminApi.copyRentRoll(rentRollId)
    },
    copyOperatingStatement(operatingStatementId: number) {
      return adminApi.copyOperatingStatement(operatingStatementId)
    },
    /**
     * Run a gjson query against the generated json payload (for Excel templating)
     * @param query - The gjson query to run
     * @returns The result of the query
     */
    async testJsonQuery(payload: string, query: string): Promise<unknown> {
      const client = await coreClient()
      return client.aiReports.runQuery({
        data: payload,
        query
      })
    },
    debugProperties(ids: string[]) {
      return adminApi.debugProperties(ids)
    },
    getCustomerSubscriptions(userId: string) {
      return adminApi.getCustomerSubscriptions(userId)
    },
    getChildSubscriptions(userId: string) {
      return adminApi.getChildSubscriptions(userId)
    },
    closeChildAccount(userId: string, subscriptionId: string) {
      return adminApi.closeChildAccount(userId, subscriptionId)
    },
    keepChildAccount(userId: string, subscriptionId: string) {
      return adminApi.keepChildAccount(userId, subscriptionId)
    },
    getOrganizationReports(organizationId: number, userId: string) {
      return adminApi.listOrganizationReports(organizationId, userId)
    },
    toggleOrganizationPause(orgId: number) {
      return adminApi.toggleOrganizationPause(orgId)
    },
    async clearAllCache() {
      const client = await coreClient()
      return client.admin.clearCache()
    },
    async clearCacheByState(state: string) {
      const client = await coreClient()
      return client.admin.clearCacheByState(state)
    },

    // SAML SSO
    createSso: adminApi.createSso,
    viewSso: adminApi.viewSso,
    setSsoStatus: adminApi.setSsoStatus,

    // Organization Group management
    getOrganizationGroups: adminApi.getOrganizationGroups,
    createOrganizationGroup: adminApi.createOrganizationGroup,
    updateOrganizationGroup: adminApi.updateOrganizationGroup,
    deleteOrganizationGroup: adminApi.deleteOrganizationGroup,
    createOrganizationGroupUser: adminApi.createOrganizationGroupUser,
    deleteOrganizationGroupUser: adminApi.deleteOrganizationGroupUser,
    createOrganizationGroupDefault: adminApi.createOrganizationGroupDefault,
    setOrganizationGroupExternal: adminApi.setOrganizationGroupExternal
  }
})
